import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  CubeIcon,
  FlagIcon,
  IdentificationIcon,
  KeyIcon,
  LifebuoyIcon,
  MegaphoneIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  ShareIcon,
  Square3Stack3DIcon,
  Squares2X2Icon,
  TicketIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { type ComponentType, type SVGProps } from 'react';

import { FINANCE_NAV_PERMISSIONS } from '@/lib/use-finance-access';

/**
 * A bal-sidebar — **jog-vezérelt**: minden menüpont egy feature-joghoz (`{resource}.view`) kötött; a UI a
 * `/session` permissions-e (vagy platform-admin bypass) alapján szűr. `to` nélküli pont = még nincs Atlas-route
 * → „hamarosan" (letiltva). `labelKey` az i18n-kulcs. A csoportosítás iparági-best-practice domének szerint:
 * Ügyfélkapcsolat (kikkel) · Szolgáltatások (mit adunk el) · Pénzügy (mennyi jön be és megy ki) · Támogatás és kommunikáció ·
 * Szervezet (belső) · Adminisztráció. **Minden csoportnak van fejléce** (fejléc-nélküli szakasz vizuálisan a
 * fölötte lévő csoporthoz tapadna). A jogosultságok-oldal modálja UGYANEZT a csoportosítást tükrözi
 * (a jogok `group`/`position` metaadatából: `apps/api/seed/atlas-rbac.json` → Odin RBAC-katalógus) — a
 * kettőt EGYÜTT kell módosítani, és egy statikus fal méri, hogy a jog-sorrend a menü sorrendjét követi
 * (`apps/api/src/rbac-sync/atlas-rbac.manifest.spec.ts`).
 */
export interface NavItem {
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** A megkövetelt feature-jog (üresen: mindig látszik, pl. dev-eszköz). */
  permission?: string;
  /**
   * TÖBB-FÜLES lap: elég az egyik jog. A lap maga fülenként kapuz (a fül csak a saját jogával látszik), a
   * MENÜPONT viszont már egyetlen fül jogára is utat kell hogy adjon — különben egy csak-katalógus-jogú
   * felhasználó elől eltűnne az egyetlen bejárat. A kettő közül pontosan az egyiket add meg.
   */
  anyPermission?: readonly string[];
  /** Cél-route — hiányában „hamarosan" (letiltva). */
  to?: string;
  /** Rövid leírás i18n-kulcsa — a navbarban a cím alatt jelenik meg (a lap NEM ismétli). */
  descKey?: string;
}
export interface NavGroup {
  /** Csoport-fejléc i18n-kulcsa (hiányában: fejléc nélküli, lapos szakasz). */
  labelKey?: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  {
    items: [
      {
        // Áttekintő: MINDEN belépett user látja (nincs külön jog); a felület tartalmát a szerepkör dönti el.
        // ALCÍM SZÁNDÉKOSAN NINCS (user-döntés): a kezdőlap fejléce maga a napszak-köszöntés, egy
        // magyarázó alcím a topbarban csak zaj (az alcím a LISTA-oldalak affordanciája).
        labelKey: 'nav.dashboard',
        icon: Squares2X2Icon,
        to: '/',
      },
    ],
  },
  {
    labelKey: 'nav.group.clientMgmt',
    items: [
      {
        labelKey: 'nav.clients',
        icon: BriefcaseIcon,
        permission: 'clients.view',
        to: '/clients',
        descKey: 'nav.desc.clients',
      },
      {
        labelKey: 'nav.accounts',
        icon: BuildingOffice2Icon,
        permission: 'accounts.view',
        to: '/accounts',
        descKey: 'nav.desc.accounts',
      },
      {
        // SZOLGÁLTATÁSOK — a hozzárendelés ügyfélhez/fiókhoz tartozik, ezért ITT a helye (nem külön,
        // kételemű menücsoportban). A lap KÉT FÜLES: hozzárendelések + katalógus; a katalógusnak ezért
        // nincs külön menüpontja. A menüpont BÁRMELYIK fül jogára látszik — a katalógus-jog önmagában is
        // utat ad a lapra (a héj ilyenkor a katalógus-fülre esik).
        labelKey: 'nav.services',
        icon: Square3Stack3DIcon,
        anyPermission: ['service_assignments.view', 'service_catalog.view'],
        to: '/services',
        descKey: 'nav.desc.services',
      },
      {
        // BANKKÁRTYÁK — a PÉNZÜGY csoportból ide költözve (mérve): a SZÁMLÁZÁS egyetlen ponton sem hivatkozik
        // a kártyára (`invoices` + `invoice-generation`: nulla `paymentCard`-találat). Az EGYETLEN fogyasztója
        // a szolgáltatás-hozzárendelés (`paymentCardId` — melyik kártyáról megy a díj) és a saját
        // lejárat-figyelő cronja, ami a címzettet is a hozzárendelésen át oldja fel. A kártya tehát
        // ÜGYFÉL-TÖRZSADAT + egy figyelés, nem számlázási artefaktum — a Pénzügy csoportban az marad, ami a
        // SZÁMLÁT alkotja (a számlázási profil például igen: az a számlázott fél).
        labelKey: 'nav.paymentCards',
        icon: CreditCardIcon,
        permission: 'payment_cards.view',
        to: '/payment-cards',
        descKey: 'nav.desc.paymentCards',
      },
      {
        labelKey: 'nav.contacts',
        icon: UserCircleIcon,
        permission: 'contacts.view',
        to: '/contacts',
        descKey: 'nav.desc.contacts',
      },
    ],
  },
  {
    // PÉNZÜGY (korábban „Számlázás") — a csoport BELSŐ rendje az iparági bevett sorrend: BIZONYLAT (számlák) →
    // a generátor bemenete (használati adatok) → TÖRZSADAT (profilok, kártyák) → RIPORT (bevétel/kiadás).
    // A bevétel/kiadás azért UTOLSÓ, mert az összegző nézet: a fölötte lévő lapok adatából áll össze.
    labelKey: 'nav.group.finance',
    items: [
      {
        labelKey: 'nav.invoices',
        icon: ReceiptPercentIcon,
        permission: 'invoices.view',
        to: '/invoices',
        descKey: 'nav.desc.invoices',
      },
      {
        labelKey: 'nav.usageData',
        icon: ChartBarIcon,
        permission: 'usage_data.view',
        to: '/usage-data',
        descKey: 'nav.desc.usageData',
      },
      {
        labelKey: 'nav.billingProfiles',
        icon: IdentificationIcon,
        permission: 'billing_profiles.view',
        to: '/billing-profiles',
        descKey: 'nav.desc.billingProfiles',
      },
      // BEVÉTEL/KIADÁS — a korábbi „Bevétel-előrejelzés", a Szolgáltatások csoportból ide költözve: nem azt
      // mondja meg, MIT adunk el, hanem hogy MENNYI pénz jön be és megy ki.
      {
        labelKey: 'nav.finance',
        icon: ScaleIcon,
        // a Bevétel és a Kiadás külön jog — a menüpont bármelyikkel látszik (a lap fülenként kapuz)
        anyPermission: FINANCE_NAV_PERMISSIONS,
        to: '/finance',
        descKey: 'nav.desc.finance',
      },
    ],
  },
  {
    labelKey: 'nav.group.support',
    items: [
      {
        labelKey: 'nav.tickets',
        icon: TicketIcon,
        permission: 'tickets.view',
        to: '/tickets',
        descKey: 'nav.desc.tickets',
      },
      {
        labelKey: 'nav.messages',
        icon: ChatBubbleLeftRightIcon,
        permission: 'messages.view',
        to: '/messages',
        descKey: 'nav.desc.messages',
      },
      {
        labelKey: 'nav.meetings',
        icon: CalendarIcon,
        permission: 'meetings.view',
        to: '/meetings',
        descKey: 'nav.desc.meetings',
      },
      // A KAMPÁNY-ELŐREJELZÉS a Megbeszélések és a Fejlesztések KÖZÉ került (user-döntés): az account-csapat
      // tervezési eszköze — napi munka, mint a fölötte lévők, de nem ügyfél-kommunikáció.
      {
        labelKey: 'nav.campaignForecast',
        icon: FlagIcon,
        permission: 'campaign_forecast.view',
        to: '/campaign-forecast',
        descKey: 'nav.desc.campaignForecast',
      },
      // A FEJLESZTÉSEK a szekció UTOLSÓ eleme (user-döntés): a jegy/üzenet/megbeszélés napi munka, ez pedig
      // a cég hírfolyama — ritkábban használt, de ide tartozik (a partnerrel is ezt osztjuk meg).
      {
        labelKey: 'nav.productUpdates',
        icon: MegaphoneIcon,
        permission: 'product_updates.view',
        to: '/product-updates',
        descKey: 'nav.desc.productUpdates',
      },
    ],
  },
  {
    labelKey: 'nav.group.organization',
    items: [
      {
        labelKey: 'nav.orgStructure',
        icon: ShareIcon,
        permission: 'organizational_structure.view',
        to: '/org-structure',
        descKey: 'nav.desc.orgStructure',
      },
      {
        labelKey: 'nav.employees',
        icon: UsersIcon,
        permission: 'employees.view',
        to: '/employees',
        descKey: 'nav.desc.employees',
      },
      {
        labelKey: 'nav.supportTeams',
        icon: LifebuoyIcon,
        permission: 'support_teams.view',
        to: '/support-teams',
        descKey: 'nav.desc.supportTeams',
      },
      {
        labelKey: 'nav.assets',
        icon: CubeIcon,
        permission: 'asset_inventory.view',
        to: '/assets',
        descKey: 'nav.desc.assets',
      },
    ],
  },
  {
    labelKey: 'nav.group.admin',
    items: [
      {
        labelKey: 'nav.accessRights',
        icon: KeyIcon,
        permission: 'access_rights.view',
        to: '/access-rights',
        descKey: 'nav.desc.accessRights',
      },
      {
        labelKey: 'nav.activityLog',
        icon: ClipboardDocumentListIcon,
        permission: 'activity_log.view',
        to: '/activity-log',
        descKey: 'nav.desc.activityLog',
      },
    ],
  },
];

/**
 * LÁTHATÓ-E a menüpont a jogaival? KÉT fogyasztója van (az oldalsáv és a ⌘K-paletta), ezért itt él, nem
 * mindkettőben lemásolva: egy elfelejtett `anyPermission`-ág a palettán NÉMÁN adna utat egy olyan lapra,
 * amihez nincs jog (a lap maga kapuz, de a hamis affordancia így is megmarad).
 */
export function navItemVisible(item: NavItem, can: (permission: string) => boolean): boolean {
  if (item.anyPermission) return item.anyPermission.some((p) => can(p));
  return item.permission ? can(item.permission) : true;
}
